import * as React from 'react';

// =====================================================================
// useFocusTrap — keeps Tab / Shift+Tab focus inside a container while
// it is active (modals, dialogs). Without this a keyboard user can Tab
// straight out of an `aria-modal` dialog into the obscured page behind
// it — a WCAG 2.4.3 (Focus Order) failure that the aria-modal attribute
// promises is handled but the browser does NOT enforce on its own.
//
// Attach to the dialog card ref; pass `active` = the open state. The
// trap re-reads focusable children on each Tab, so dynamically added
// fields (e.g. an async-loaded form) are picked up without re-binding.
// =====================================================================

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  active: boolean,
): void {
  React.useEffect(() => {
    if (!active) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const container = containerRef.current;
      if (!container) return;

      const focusable = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusable.length === 0) {
        // Nothing focusable inside — keep focus on the container itself.
        e.preventDefault();
        container.focus();
        return;
      }

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const activeEl = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        if (activeEl === first || !container.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (activeEl === last || !container.contains(activeEl)) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [active, containerRef]);
}
